import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrgStore } from '@Models/useOrgModel';
import { DeleteApprovedUsersDto } from '@Types/globalSetting';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import LoadingBtn from '@Components/Common/LoadingBtn';
import { TransferChildRef } from '../../Common/TransferForAddTargets/exchange';
import PolicyTypeSelect from '../ApprovedUsers/AddUserModal/PolicyType';
import { useOneDriveTargetUserGroups } from '@Policy/hooks';
import { SingleTargetUserGroups } from '@Policy/Components/SingleTargetUserGroups';
import { PolicyProvider } from '@Policy/store';
import { getOneDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.oneDrive';
import {
  useApprovedOneDriveUsersMutations,
  useApprovedOneDriveUsersQuery,
} from './hooks';

const TargetUserGroupsOneDrive = React.forwardRef<TransferChildRef>(
  (_, ref) => {
    const props = useOneDriveTargetUserGroups();
    return <SingleTargetUserGroups {...props} ref={ref} />;
  }
);

TargetUserGroupsOneDrive.displayName = 'TargetUserGroupsOneDrive';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyType: PolicyTypeEnum;
  currentUsersCount: number;
}

const MAX_APPROVED_USERS = 1024;

const AddUserModal = ({ isOpen, onClose, policyType }: AddUserModalProps) => {
  const { t } = useTranslation(['globalSetting', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const childRef = useRef<TransferChildRef | null>(null);
  const { orgId, orgsMap } = useOrgStore();
  const policyTypeRef = useRef<any>(null);
  const { addUsersMutation } = useApprovedOneDriveUsersMutations(policyType);
  const { data: existingUsers = [] } =
    useApprovedOneDriveUsersQuery(policyType);

  const existingUserKeys = useMemo(
    () => new Set(existingUsers.map((u) => `${u.policyType}#${u.email}`)),
    [existingUsers]
  );

  const templateData = getOneDriveTemplateConfig(t, '');
  const configData = {};

  const handleSave = async () => {
    const selectedPolicyType = policyTypeRef.current?.getValue();
    if (
      childRef.current === null ||
      childRef.current.checkedKeys.length === 0
    ) {
      return;
    }

    const allUsers: DeleteApprovedUsersDto[] = [];
    childRef.current.checkedKeys.forEach((key) => {
      allUsers.push({
        name: key.title as string,
        companyGuid: orgId,
        policyType: Number(selectedPolicyType),
        email: key.key as string, // Use UPN (TreeNode.key = userId) for policymatch alignment
      });
    });

    // Deduplicate: filter out users that already exist
    const newUsers = allUsers.filter(
      (u) => !existingUserKeys.has(`${u.policyType}#${u.email}`)
    );

    if (newUsers.length === 0) {
      window.showNotification({
        titleMsg: t('globalSetting.approvedUser.add.allDuplicate'),
        severity: 'warning',
      });
      return;
    }

    if (existingUsers.length + newUsers.length > MAX_APPROVED_USERS) {
      window.showNotification({
        titleMsg: t('globalSetting.approvedUser.add.limitExceeded', {
          count: MAX_APPROVED_USERS,
        }),
        severity: 'error',
      });
      return;
    }

    try {
      const skippedCount = allUsers.length - newUsers.length;
      await addUsersMutation.mutateAsync(newUsers);
      window.showNotification({
        titleMsg:
          skippedCount > 0
            ? t('globalSetting.approvedUser.add.successWithSkipped', {
                skippedCount,
              })
            : t('globalSetting.DNSDetectionExceptionList.add.success'),
        severity: 'success',
      });
      onClose();
    } catch {
      // Error handling is done in the mutation hook
    }
  };

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>
          {t('globalSetting.approvedOneDriveUser.addModal.title')}
        </ModalHeader>
        <ModalBody>
          <Box>
            <Flex>
              <Text
                color={colorStyle.color.secondary}
                lineHeight="lg"
                maxWidth={1000}
              >
                {t('globalSetting.approvedOneDriveUser.addModal.description')}
              </Text>
            </Flex>
            <Flex alignItems="center" m="8px 0px 12px 0px">
              <Text fontWeight={600}>
                {t('globalSetting.approvedUser.column.policyType')}:
              </Text>
              <Space width="12px" />
              <PolicyTypeSelect ref={policyTypeRef} />
            </Flex>
            <Box m="12px 0px">
              <Text>
                {t('globalSetting.approvedOneDriveUser.addModal.currentOrg', {
                  orgName: orgsMap.get(orgId) || '',
                })}
              </Text>
            </Box>
            <PolicyProvider
              template={templateData}
              config={configData}
              orgId={orgId}
            >
              <TargetUserGroupsOneDrive ref={childRef} />
            </PolicyProvider>
          </Box>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn
            loading={addUsersMutation.isPending}
            onClick={handleSave}
            variant="primary"
          >
            {t('common.button.ok')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddUserModal;
